import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { formattedTypeContainer } from "../../../components/formatted/FormattedText";
import { CharacterizationLoader } from "../../../components/loader/CharacterizationLoader";

const ContainerSettingsRow = ({
  container,
  setModalEdit,
  setFormData,
  onDuplicate,
  isDuplicating = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const showDuplicate =
    Boolean(onDuplicate) && !container?.fictiveContainer?.active;

  const navigateToZoneSettings = () => {
    const id = container?._id || container?._measurement;
    if (id) {
      history.push(`/settings/containers/${id}`, {
        container,
        from: "settings",
      });
    } else {
      setModalEdit?.(true);
      setFormData?.(container);
    }
  };

  const volumeLabel = useMemo(() => {
    if (container?.volume > 0) {
      return `${container.volume} ${container.volumeUnit || "m3"}`;
    }
    if (container?.volumeToCubicMeter) {
      return `${container.volumeToCubicMeter} m3`;
    }
    return "-";
  }, [container?.volume, container?.volumeUnit, container?.volumeToCubicMeter]);

  const statusItems = [
    {
      key: "sorting",
      label: t("containerSettings.followUpSorting", {
        defaultValue: "Suivi du tri",
      }),
      value: container?.activatedSortingErrorOption,
    },
    {
      key: "characterization",
      label: t("containerSettings.characterization", {
        defaultValue: "Caractérisation",
      }),
      value: container?.characterization,
    },
    {
      key: "filling",
      label: t("containerSettings.followUpFilling", {
        defaultValue: "Suivi du remplissage",
      }),
      value: container?.activatedFillingLevelOption,
    },
    {
      key: "compact",
      label: t("containerSettings.followUpCompaction", {
        defaultValue: "Suivi du compactage",
      }),
      value: container?.packmatActive,
    },
  ];

  const handleRowClick = (e) => {
    if (
      e.target.closest(".table-standard-row-actions") ||
      e.target.closest(".table-standard-row-actions-dots") ||
      e.target.closest(".action-choice")
    ) {
      return;
    }
    navigateToZoneSettings();
  };

  return (
    <article
      className={`table-standard-row container-settings-line${isDuplicating ? " table-standard-row--busy" : ""}`}
      onClick={handleRowClick}
      aria-busy={isDuplicating}
    >
      {isDuplicating ? (
        <div
          className="table-standard-row-busy-overlay"
          role="status"
          aria-live="polite"
        >
          <CharacterizationLoader size="sm" />
          <span className="table-standard-row-busy-overlay__label">
            {t("settingsContainers.duplicating")}
          </span>
        </div>
      ) : null}
      <div className="table-standard-row-cell table-standard-cell-align-left">
        <div className="container-settings-cell-stack min-w-0">
          <span className="container-settings-cell-primary container-address-name table-standard-row-cell-truncate">
            {container?.addressName || "-"}
          </span>
          <span
            className={`container-settings-cell-secondary container-positioning table-standard-row-cell-truncate${
              container?.positioning
                ? ""
                : " container-settings-cell-secondary--placeholder"
            }`}
          >
            {container?.positioning || "\u00a0"}
          </span>
        </div>
      </div>

      <div className="table-standard-row-cell table-standard-cell-align-left">
        <div className="container-settings-cell-stack min-w-0">
          <span className="container-settings-cell-primary table-standard-row-cell-truncate">
            {container?.wasteName || "-"}
          </span>
          <span
            className="container-settings-cell-secondary container-settings-cell-secondary--placeholder table-standard-row-cell-truncate"
            aria-hidden="true"
          >
            {"\u00a0"}
          </span>
        </div>
      </div>

      <div className="table-standard-row-cell table-standard-cell-align-left">
        <div className="container-settings-cell-stack min-w-0">
          <span className="container-settings-cell-primary table-standard-row-cell-truncate">
            {formattedTypeContainer(container.type)}
          </span>
          <span className="container-settings-cell-secondary container-type-meta table-standard-row-cell-truncate">
            {volumeLabel}
          </span>
        </div>
      </div>

      <div className="table-standard-row-cell table-standard-cell-align-left">
        <div className="container-settings-cell-modules min-w-0">
          <div className="container-status-group">
            {statusItems.map(({ key, label, value }) => (
              <span
                key={key}
                className={`container-status-badge ${
                  value
                    ? "container-status-badge--ok"
                    : "container-status-badge--ko"
                }`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {showDuplicate ? (
        <article className="table-standard-row-cell table-standard-row-actions">
          <div className="table-standard-row-actions-menu-anchor">
            <button
              className="table-standard-row-actions-dots"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              aria-label={t("Actions")}
              aria-expanded={isOpen}
            >
              <i
                className={`fas ${!isOpen ? "fa-ellipsis-v" : "fa-times"}`}
              ></i>
            </button>
            <div className={`action-choice ${isOpen ? "display-action" : ""}`}>
              <button
                type="button"
                className="action"
                disabled={isDuplicating}
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(container);
                  setIsOpen(false);
                }}
              >
                {t("settingsContainers.duplicate")}
              </button>
            </div>
          </div>
        </article>
      ) : (
        <article className="table-standard-row-cell table-standard-row-actions" />
      )}
    </article>
  );
};

export default ContainerSettingsRow;
